import { message } from "antd";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getFileDownloadURL } from "services/file-mgmt";
import ErrorBoundary from "components/error-boundary";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import CiqPdfViewer from "./ciq-pdf-viewer";
import PdfLoaderDiv from "./pdf-loader";
import { BoundingBoxPositionType } from "./models";

function CiqDocumentView({
  blobKey,
  boundingBoxPossition,
  auditFeatureType = null,
  filename = "",
  attachmentId
}: {
  blobKey: string;
  boundingBoxPossition?: BoundingBoxPositionType;
  /** featureType for audit log (e.g. MATERIALS). */
  auditFeatureType?: string | null;
  filename?: string;
  attachmentId?: string;
}) {
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [error, setError] = useState(false);
  const { gqlClientForProject }: TProjectContext = useContext(ProjectContext);
  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);

  async function init(logView = true) {
    const fileDownloadURLResp: any = await getFileDownloadURL([blobKey]);
    if (fileDownloadURLResp.data) {
      const link = fileDownloadURLResp.data.success.urls[blobKey];
      setUrl(link);
      if (logView) {
        saveAuditLogEvent({
          eventType: "FILE_ATTACHMENT_VIEW",
          featureType: auditFeatureType,
          // init(logView=true) only runs behind the `if (blobKey)` guard in the
          // effect below, so blobKey is always truthy here and the "" fallback
          // is unreachable.
          /* v8 ignore next */
          filename: filename || blobKey || "",
          ...(attachmentId ? { attachmentId } : {})
        });
      }
    }
    if (fileDownloadURLResp.error) {
      setError(true);
      message.error(fileDownloadURLResp.error.msg);
    }
  }

  const initRef = useRef(init);
  initRef.current = init;

  useEffect(() => {
    setUrl(undefined);
    setError(false);
    if (blobKey) initRef.current();
  }, [blobKey]);

  // Must keep a stable identity: CiqPdfViewer reloads the whole PDF when this
  // callback changes, so a new function on every render makes the viewer blink.
  const onExpiredUrl = useCallback(() => {
    setUrl(undefined);
    setError(false);
    initRef.current(false);
  }, []);

  if (error) return <div>Something wrong</div>;
  if (!url) return <PdfLoaderDiv />;

  return (
    <ErrorBoundary>
      <CiqPdfViewer
        fileUrl={url}
        onExpiredUrl={onExpiredUrl}
        boundingBoxPossition={boundingBoxPossition}
      />
    </ErrorBoundary>
  );
}

export default CiqDocumentView;
